import TextButton from "@/components/textButton/TextButton";
import { translate } from "@/config/translator";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Attendance } from "@/api/openapi";
import { Dispatch, SetStateAction, useContext } from "react";
import { clsx } from "clsx/lite";
import { NO_VALUE } from "@/config/constants";
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { SearchableOptions } from "@/components/searchableOptions/searchableOptions";
import { KeyLabelPair } from "@/config/types";
import {
  makeCellId,
  onMouseEnterCell,
  onMouseLeaveCell,
} from "./useEmployeeAttendanceRow";
import { PspContext } from "@/config/contexts";

type AttendanceDesktopAddGiustificativoRowProps = {
  collapsibleCellClasses: string;
  employeeAttendance: Attendance;
  employeeIndex: number;
  giustificativoSearchQuery: string;
  onChangeGiustificativoSearchQuery: (query: string) => void;
  giustificativiOptions: KeyLabelPair[];
  onAddGiustificativo: (value: string | null) => void;
  addGiustificativoPopoverOpen: boolean;
  setAddGiustificativoPopoverOpen: Dispatch<SetStateAction<boolean>>;
  isExpanded: boolean;
};

export function AttendanceDesktopAddGiustificativoRow({
  collapsibleCellClasses,
  employeeAttendance,
  employeeIndex,
  giustificativoSearchQuery,
  giustificativiOptions,
  onAddGiustificativo,
  onChangeGiustificativoSearchQuery,
  addGiustificativoPopoverOpen,
  setAddGiustificativoPopoverOpen,
  isExpanded,
}: AttendanceDesktopAddGiustificativoRowProps) {
  const { lang } = useContext(PspContext);

  return (
    <tr className={clsx(isExpanded ? "table-row" : "none")}>
      <td
        colSpan={2}
        className={clsx(
          "sticky",
          "left-0",
          "z-10",
          "border-r",
          "border-r-neutral",
          "shadow-sm",
          "bg-white",
        )}
        id={makeCellId(
          employeeIndex,
          -1,
          employeeAttendance.days[0].giustificativi.length,
        )}
        onMouseEnter={() =>
          onMouseEnterCell(
            employeeIndex,
            -1,
            employeeAttendance.days[0].giustificativi.length,
            employeeIndex,
            employeeAttendance.days[0].giustificativi.length,
          )
        }
        onMouseLeave={() =>
          onMouseLeaveCell(
            employeeIndex,
            -1,
            employeeAttendance.days[0].giustificativi.length,
            employeeIndex,
            employeeAttendance.days[0].giustificativi.length,
          )
        }
      >
        <div
          className={clsx(
            collapsibleCellClasses,
            "h-12",
            "border-l-2",
            "border-l-primary",
            "px-4",
          )}
        >
          <Popover
            open={addGiustificativoPopoverOpen}
            onOpenChange={(_, { open }) =>
              setAddGiustificativoPopoverOpen(open)
            }
          >
            <PopoverTrigger>
              <TextButton onClick={() => setAddGiustificativoPopoverOpen(true)}>
                <div className="flex flex-row gap-2 items-center text-base">
                  <div>{translate("add", lang)}</div>
                  <FontAwesomeIcon icon={faPlus} />
                </div>
              </TextButton>
            </PopoverTrigger>
            <PopoverSurface>
              <SearchableOptions
                searchQuery={giustificativoSearchQuery}
                multiSelect={false}
                value={null}
                options={giustificativiOptions}
                onChange={(value) => onAddGiustificativo(value)}
                onChangeSearchQuery={onChangeGiustificativoSearchQuery}
                label={translate("search", lang)}
              />
            </PopoverSurface>
          </Popover>
        </div>
      </td>
      {employeeAttendance.days.map((day, dayIndex) => (
        <td
          key={`employee-attendance-day-${dayIndex}`}
          className={clsx(day.isHoliday ? "bg-canvas-secondary" : "bg-white")}
          id={makeCellId(
            employeeIndex,
            dayIndex,
            employeeAttendance.days[0].giustificativi.length,
          )}
          onMouseEnter={() =>
            onMouseEnterCell(
              employeeIndex,
              dayIndex,
              employeeAttendance.days[0].giustificativi.length,
              employeeIndex,
              employeeAttendance.days[0].giustificativi.length,
            )
          }
          onMouseLeave={() =>
            onMouseLeaveCell(
              employeeIndex,
              dayIndex,
              employeeAttendance.days[0].giustificativi.length,
              employeeIndex,
              employeeAttendance.days[0].giustificativi.length,
            )
          }
        >
          <div
            className={clsx(
              collapsibleCellClasses,
              "h-12",
              "flex",
              "flex-row",
              "justify-center",
              "text-font-secondary",
              dayIndex !== employeeAttendance.days.length - 1 && "border-r",
              "border-neutral",
            )}
          >
            {NO_VALUE}
          </div>
        </td>
      ))}
      <td
        className={clsx(
          "min-w-16",
          "sticky",
          "right-30",
          "z-10",
          "shadow-md",
          "h-full",
          "border-neutral",
          "border-l",
        )}
      >
        <div
          className={clsx(
            collapsibleCellClasses,
            "h-12",
            "bg-white",
            "flex",
            "flex-row",
            "justify-center",
            "text-font-secondary",
          )}
        >
          {NO_VALUE}
        </div>
      </td>
      <td className={clsx("min-w-30", "sticky", "right-0", "h-full", "z-10")}>
        <div
          className={clsx(
            collapsibleCellClasses,
            "h-12",
            "bg-white",
            "border-x",
            "border-neutral",
          )}
        />
      </td>
    </tr>
  );
}
